import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

const Modal = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  children,
}) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeStyles = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
  };

  return (
    <>
      {/* Backdrop with dual-theme support */}
      <div
        className="fixed inset-0 bg-brand-darker dark:bg-brand-darker bg-opacity-50 dark:bg-opacity-75 z-40 transition-opacity duration-slow animate-fadeIn"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal Container */}
      <div 
        className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
      >
        <div
          className={`bg-light-primary dark:bg-brand-elevated rounded-xl shadow-xl ${sizeStyles[size]} w-full transform transition-all duration-slow animate-scaleIn`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with dual-theme support */}
          {title && (
            <div className="flex items-center justify-between p-6 border-b border-light-border-default dark:border-brand-border-default">
              <h2 id="modal-title" className="text-2xl font-bold text-light-text-primary dark:text-brand-text-primary">{title}</h2>
              <button
                onClick={onClose}
                className="text-light-text-secondary dark:text-brand-text-secondary hover:text-light-text-primary dark:hover:text-brand-text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-light-accent-red dark:focus:ring-brand-accent-red rounded"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
          
          {/* Content */}
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    </>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  children: PropTypes.node.isRequired,
};

export default Modal;
